import { useState } from 'react'
import Menu from '@mui/material/Menu'
import styled from 'styled-components'
import MenuItem from '@mui/material/MenuItem'
import { useTranslation } from 'react-i18next'
import { Filter, FilterCheck, FilterCloseIcon } from '@/components/icons'
import { colors, fontWeights } from '@/styles'

const Container = styled.div``

const TopContainer = styled.div`
  padding: 8px 0;
`

const ButtonWrapper = styled.div`
  position: relative;
  cursor: pointer;
  width: 100%;
`

const ActiveDot = styled.div`
  position: absolute;
  top: -1px;
  right: -1px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: ${colors.red56};
`

const ItemContainer = styled.div`
  display: flex;
  margin: 3px 4px;
  padding: 4px 11px;
  border-radius: 8px;
  background-color: ${props => (props.active ? colors.blue96 : colors.white)};
  align-items: center;
  :hover {
    background-color: ${props => (props.active ? 'none' : 'rgba(0, 0, 0, 0.3)')};
  }
`

const UnreadWrapper = styled(ButtonWrapper)`
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: 4px 0;
`
const FavouriteWrapper = styled(ButtonWrapper)`
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 4px;
`

const UnreadContainer = styled(ItemContainer)`
  padding: 4px 11px;
`

const IconWrapper = styled.div`
  margin-right: 10px;
  width: 10px;
  height: 24px;
`

const ItemText = styled.span`
  font-size: 14px;
  color: ${colors.grey24};
  font-weight: ${props => (props.active ? fontWeights.bold : fontWeights.regular)};
`

const TopContainerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 12px;
  font-size: 12px;
  font-weight: 700;
  color: ${colors.grey56};
`

const FilterCloseIconWrapper = styled.div`
  cursor: pointer;
`

const FilterDropdown = ({ params, setParams, filters, filterActive }) => {
  const showUnread = params?.unread === '1'
  const showFavourite = params?.favourite === '1'
  const [anchorEl, setAnchorEl] = useState(null)
  const open = !!anchorEl
  const handleClick = event => setAnchorEl(event.currentTarget)

  const handleClose = () => setAnchorEl(null)

  const { t } = useTranslation()

  const renderAllButton = () => {
    const active = !params?.filter
    return (
      <MenuItem key={active}>
        <ButtonWrapper>
          <ItemContainer active={active} onClick={() => (active ? {} : setParams('filter', null))}>
            <IconWrapper>{active && <FilterCheck />}</IconWrapper>
            <ItemText active={active}>
              {t('l.chat.view.all_messages.title', 'All Messages')}
            </ItemText>
          </ItemContainer>
        </ButtonWrapper>
      </MenuItem>
    )
  }
  return (
    <Container>
      <ButtonWrapper onClick={handleClick}>
        {filterActive && <ActiveDot />}
        <Filter />
      </ButtonWrapper>
      <Menu
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        anchorEl={anchorEl}
        onClose={handleClose}
        open={open}
        sx={{
          '& .MuiPaper-root': {
            border: `1.5px solid ${colors.grey88}`,
            boxShadow:
              '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
            width: 222,
            borderRadius: '6px'
          },
          '& .MuiMenuItem-root': {
            padding: 0
          },
          '& .MuiList-root': {
            padding: '0 0 8px'
          },

          '& .MuiButtonBase-root:hover': {
            backgroundColor: colors.white
          }
        }}
      >
        <TopContainer>
          <TopContainerHeader>
            <span>{t('b.chat.view.filter.header.text', 'Filter by')}:</span>
            <FilterCloseIconWrapper onClick={handleClose}>
              <FilterCloseIcon />
            </FilterCloseIconWrapper>
          </TopContainerHeader>
          {renderAllButton()}
          {filters.map(filter => {
            const active = params?.filter === filter.id
            return (
              <MenuItem key={filter.id}>
                <ButtonWrapper>
                  <ItemContainer
                    active={active}
                    onClick={() => setParams('filter', active ? null : filter.id)}
                  >
                    <IconWrapper>{active && <FilterCheck />}</IconWrapper>
                    <ItemText active={active}>{filter?.title}</ItemText>
                  </ItemContainer>
                </ButtonWrapper>
              </MenuItem>
            )
          })}
        </TopContainer>

        <MenuItem>
          <UnreadWrapper>
            <UnreadContainer
              active={showUnread}
              onClick={() => setParams('unread', showUnread ? null : 1)}
            >
              <IconWrapper>{showUnread && <FilterCheck />}</IconWrapper>
              <ItemText active={showUnread}>{t('b.chat.view.unread.button', 'Unread')}</ItemText>
            </UnreadContainer>
          </UnreadWrapper>
        </MenuItem>
        <MenuItem>
          <FavouriteWrapper>
            <UnreadContainer
              active={showFavourite}
              onClick={() => setParams('favourite', showFavourite ? null : 1)}
            >
              <IconWrapper>{showFavourite && <FilterCheck />}</IconWrapper>
              <ItemText active={showFavourite}>
                {t('b.chat.view.favourites.button', 'Favourites')}
              </ItemText>
            </UnreadContainer>
          </FavouriteWrapper>
        </MenuItem>
      </Menu>
    </Container>
  )
}

export default FilterDropdown
